import { useCallback } from 'react'
import { useHistory, useParams } from 'react-router-dom'

import { useVersioning } from '../../versioning'
import { getPermanentPageId } from '../../shares/utils'
import { useAnalytics, RouteParams } from '@sketch/modules-common'
import { useIsPreviewView } from '../../shares/views/PreviewView/PreviewContext'
import { useNavigateToPreviewSection } from './useNavigateToPreviewSection'
import type { GoToArtboardEventActionType } from '@sketch/canvas'

/**
 * Returns a navigateToSection callback that focuses the canvas on a single
 * Section, hiding every other top-level entity - the Section equivalent of
 * useNavigateToFrame.
 *
 * PAGE_SECTION is a versioned route and a Preview has no versioning context, so
 * building it there resolves to "/" and drops the viewer onto the workspace.
 * Inside a Preview we use the PREVIEW_PAGE_SECTION route instead.
 */
export function useNavigateToSection() {
  const isPreviewView = useIsPreviewView()
  const navigateToShareSection = useNavigateToShareSection()
  const navigateToPreviewSection = useNavigateToPreviewSection()

  return isPreviewView ? navigateToPreviewSection : navigateToShareSection
}

function useNavigateToShareSection() {
  const history = useHistory()
  const { getPathname, currentVersion } = useVersioning()
  const { trackEvent } = useAnalytics()
  const { shareID, pageUUID: pageUUIDFromUrl } =
    useParams<RouteParams<'SHARE_PAGE_CANVAS_VIEW'>>()

  // On the default (first) page the URL omits the pageUUID; getPermanentPageId
  // falls back to the first document page so the PAGE_SECTION path is well-formed
  // (otherwise it becomes ".../p//sc/..." and navigation silently does nothing).
  const pageUUID = getPermanentPageId(pageUUIDFromUrl, currentVersion)

  return useCallback(
    (sectionUUID: string, actionType: GoToArtboardEventActionType) => {
      if (!pageUUID) return

      trackEvent('CANVAS - open artboard', { actionType })
      history.push(
        getPathname({
          routeKey: 'PAGE_SECTION',
          routeParams: { shareID, pageUUID, sectionUUID },
        })
      )
    },
    [trackEvent, history, getPathname, shareID, pageUUID]
  )
}
